import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { schema } from 'prose-core'
import { TextSelection } from 'prosemirror-state'
import qs from 'qs'
import { useContext, useRef, useState } from 'react'

import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { Toolbar } from 'common/ProseInput/components/Toolbar'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CommentForm } from 'components/CommentForm/CommentForm'
import { CommentFormSubmitPayload } from 'components/CommentForm/Types'
import store from 'redux/store'
import { useDocStore } from 'stores/DocStore'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { useRootStore } from 'stores/RootStore'
import { isObjectWithFunctionProperty } from 'types/unknown'
import { ourUuid } from 'utils'

import { useCollaborationDialog } from './CollaborationDialogStore'

import Styles from './NewComment.module.scss'
import { useLocation } from 'react-router-dom'

export const NewComment = observer(() => {
  const [comment, setComment] = useState('')
  const collaborationDialogStore = useCollaborationDialog()
  const almanacState = useContext(AlmanacEditorStateContext)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const [mentions, setMentions] = useState<number[]>([])

  const editorView = useEditorView()
  const [uuid, setUuid] = useState(ourUuid())
  const location = useLocation()

  const rootStore = useRootStore()
  const docStore = useDocStore()

  // Using this as a logic for commenting on the doc itself
  const noSelection = computed(() => almanacState?.selection.$from === almanacState?.selection.$to).get()

  if (!almanacState?.isSelectedNodeCommentable) return null

  const onTextSelectSubmit = async ({
    comment,
    mentionedIds,
    userId,
    richText,
  }: {
    comment: string
    mentionedIds: number[]
    richText: boolean
    userId?: string
  }) => {
    const { state, dispatch } = editorView
    let { from, to } = state.selection

    // The node type is changing after the request starts
    const nodeType = almanacState?.selectedNodeType

    try {
      await docStore.rootStore.comments.create(
        {
          layer_id: docStore.layerId,
          comment: comment.trimStart().trimEnd(),
          commentable_id: docStore.articleId,
          commentable_type: 'Article',
          uuid: noSelection ? undefined : uuid,
        },
        mentionedIds,
        richText,
        undefined,
        userId,
      )
    } catch (error) {
      if (error instanceof Error) showToastMessage(error.message, 'error')
    }

    const { tr } = state
    if (nodeType == null) {
      const mark = schema.marks.comment.create({ uuid })
      tr.addMark(from, to, mark)
      dispatch(tr)
    } else {
      const node = editorView.state.doc.nodeAt(from)

      if (node) {
        if (node.type.name === 'smartLink') {
          tr.setSelection(new TextSelection(state.doc.resolve(to + 1)))
          tr.addMark(from, to, schema.marks.comment.create({ uuid }))
          tr.setMeta('allowComments', true)
        } else if (node.type.name === 'card') {
          tr.setNodeMarkup(from, undefined, {
            ...node.attrs,
            uuids: [...node.attrs.uuids, uuid],
          })
        } else {
          tr.setMeta('allowComments', true)
          tr.setNodeMarkup(from, undefined, {
            ...node.attrs,
            uuids: [...node.attrs.uuids, uuid],
          })
        }
        dispatch(tr)
      }
    }
  }

  const scrollToComment = () => {
    const comment = Object.values(store.getState().comments.records).find((comment) => comment.uuid === uuid)
    if (comment) {
      rootStore.comments.select(comment.id)
    }
  }

  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const { userId } = params

  const handleNewCommentChange = (value: string, mentionedUsersIds: number[]) => {
    setComment(value)
    setMentions(mentionedUsersIds)
  }

  const handleSubmit = async (payload: CommentFormSubmitPayload) => {
    onTextSelectSubmit({ comment, mentionedIds: mentions, userId, richText: true })
    setUuid(ourUuid())
    if (isObjectWithFunctionProperty('clear', commentInputRef.current)) commentInputRef.current?.clear()
    // TODO: This should not need to be a timeout, we should schedule scrolls for after the document updates
    setTimeout(() => scrollToComment(), 1000)
    collaborationDialogStore.closeNewComment()
  }

  return (
    <div className={Styles.newComment}>
      <CommentForm
        autoFocus
        placeholder="Write a comment…"
        className={Styles.form}
        inputRef={commentInputRef}
        inputContainerClassName={Styles.inputContainer}
        inputClassName={Styles.input}
        value={comment}
        onChange={handleNewCommentChange}
        onSubmit={handleSubmit}
        isHtmlComment={true}
        footer={
          <Toolbar.Root>
            <Toolbar.Bold />
            <Toolbar.BulletList />
            <Toolbar.Mention />
          </Toolbar.Root>
        }
      />
    </div>
  )
})
